import { type Invoice } from "@kitchen/schemas";
import { Button, Chip, faDigits, formatDate, formatMoney } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { financeApi } from "../../api.js";
import { ErrorLine } from "../common.js";
import { PrintInvoiceView } from "../finance/print.js";

interface CustomerStatementViewProps {
  organizationId: string;
  organizationName: string;
}

export function CustomerStatementView({ organizationId, organizationName }: CustomerStatementViewProps) {
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);

  const statementQuery = useQuery({
    queryKey: ["customer", "statement", organizationId],
    queryFn: () => financeApi.getStatement(organizationId),
  });

  const invoicesQuery = useQuery({
    queryKey: ["customer", "invoices", organizationId],
    queryFn: () => financeApi.listInvoices({ organizationId, status: "issued" }),
  });

  const stmt = statementQuery.data;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="card">
        <h1 className="t-page" style={{ marginBottom: "var(--s2)" }}>
          صورتحساب مالی · {organizationName}
        </h1>
        <p className="muted t-cap">
          فهرست فاکتورهای رسمی، اسناد تعدیل (بدهکار/بستانکار) و پرداخت‌های ثبت‌شده برای سازمان شما
        </p>
      </div>

      {statementQuery.isLoading && <p className="muted">در حال بارگذاری صورتحساب...</p>}
      {statementQuery.isError && <ErrorLine error="امکان دسترسی به اطلاعات مالی این سازمان وجود ندارد." />}

      {stmt && (
        <>
          {/* Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            <div className="card">
              <span className="t-cap muted">کل فاکتورهای صادرشده</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {formatMoney(stmt.totalInvoiced, "rial")}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">کل پرداختی‌های ثبت‌شده</span>
              <div className="bold t-title" style={{ marginTop: "4px", color: "var(--blue-ink)" }}>
                {formatMoney(stmt.totalPaid, "rial")}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">مانده نهایی حساب</span>
              <div
                className="bold t-title"
                style={{
                  marginTop: "4px",
                  color: stmt.balance > 0 ? "var(--coral-strong)" : "var(--ok-ink, green)",
                }}
              >
                {stmt.balance > 0
                  ? `${formatMoney(stmt.balance, "rial")} (بدهکار)`
                  : stmt.balance < 0
                    ? `${formatMoney(Math.abs(stmt.balance), "rial")} (بستانکار)`
                    : "تسویه کامل (۰ ریال)"}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">اعتبار تخصیص‌نیافته</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {formatMoney(stmt.unallocatedCredit, "rial")}
              </div>
            </div>
          </div>

          {/* Aging Buckets */}
          <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
            <h2 className="t-title" style={{ fontSize: "16px", marginBottom: "var(--s2)" }}>
              وضعیت دیرکرد سررسید بدهی‌ها
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "var(--s3)",
              }}
            >
              <div>
                <span className="t-cap muted">۰ تا ۳۰ روز (جاری):</span>
                <div className="bold">{formatMoney(stmt.aging.current0to30, "rial")}</div>
              </div>
              <div>
                <span className="t-cap muted">۳۱ تا ۶۰ روز:</span>
                <div className="bold">{formatMoney(stmt.aging.days31to60, "rial")}</div>
              </div>
              <div>
                <span className="t-cap muted">۶۱ تا ۹۰ روز:</span>
                <div className="bold">{formatMoney(stmt.aging.days61to90, "rial")}</div>
              </div>
              <div>
                <span className="t-cap muted">بیش از ۹۰ روز:</span>
                <div
                  className="bold"
                  style={{ color: stmt.aging.days90Plus > 0 ? "var(--coral-strong)" : undefined }}
                >
                  {formatMoney(stmt.aging.days90Plus, "rial")}
                </div>
              </div>
            </div>
          </div>

          {/* Issued Invoices List */}
          <div className="card">
            <h2 className="t-title" style={{ fontSize: "18px", marginBottom: "var(--s3)" }}>
              فاکتورهای صادرشده دوره
            </h2>

            {invoicesQuery.isLoading && <p className="muted">در حال بارگذاری فاکتورها...</p>}
            {invoicesQuery.data && invoicesQuery.data.length === 0 && (
              <p className="muted">هنوز فاکتور رسمی برای این سازمان صادر نشده است.</p>
            )}

            {invoicesQuery.data && invoicesQuery.data.length > 0 && (
              <table className="table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>شماره فاکتور</th>
                    <th>دوره مصرف</th>
                    <th>مبنای محاسبه</th>
                    <th>مبلغ نهایی (ریال)</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesQuery.data.map((inv) => (
                    <tr key={inv.id}>
                      <td className="bold">{faDigits(inv.number)}</td>
                      <td>
                        از {formatDate(inv.periodStart)} تا {formatDate(inv.periodEnd)}
                      </td>
                      <td>
                        <Chip plain>{inv.basis === "delivered" ? "تحویل شده" : "سفارش"}</Chip>
                      </td>
                      <td className="bold">{formatMoney(inv.totalAmount, "rial")}</td>
                      <td>
                        <Button
                          size="md"
                          onClick={() => {
                            setActiveInvoice(inv);
                          }}
                        >
                          مشاهده و چاپ A4
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Timeline of Transactions */}
          <div className="card">
            <h2 className="t-title" style={{ fontSize: "18px", marginBottom: "var(--s3)" }}>
              ریز تراکنش‌های حساب
            </h2>

            {stmt.transactions.length === 0 ? (
              <p className="muted">تراکنشی ثبت نشده است.</p>
            ) : (
              <table className="table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>نوع سند</th>
                    <th>شماره مرجع</th>
                    <th>شرح</th>
                    <th>بدهکار</th>
                    <th>بستانکار</th>
                    <th>مانده</th>
                  </tr>
                </thead>
                <tbody>
                  {stmt.transactions.map((t) => (
                    <tr key={`${t.type}-${t.referenceId}-${t.date}`}>
                      <td>{formatDate(t.date)}</td>
                      <td>
                        <Chip
                          tone={
                            t.type === "payment"
                              ? "ok"
                              : t.type === "credit-note"
                                ? "blue"
                                : t.type === "cheque-bounced"
                                  ? "coral"
                                  : undefined
                          }
                        >
                          {t.type === "invoice"
                            ? "فاکتور"
                            : t.type === "payment"
                              ? "دریافت وجه"
                              : t.type === "credit-note"
                                ? "یادداشت بستانکار"
                                : t.type === "debit-note"
                                  ? "یادداشت بدهکار"
                                  : "برگشت چک"}
                        </Chip>
                      </td>
                      <td className="bold">{faDigits(t.referenceNumber)}</td>
                      <td>{t.description}</td>
                      <td className="bold">{t.debit > 0 ? formatMoney(t.debit, "rial") : "—"}</td>
                      <td className="bold" style={{ color: t.credit > 0 ? "var(--blue-ink)" : undefined }}>
                        {t.credit > 0 ? formatMoney(t.credit, "rial") : "—"}
                      </td>
                      <td className="bold">{formatMoney(t.balance, "rial")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* A4 Print Modal */}
      {activeInvoice && (
        <PrintInvoiceView
          invoice={activeInvoice}
          organizationName={organizationName}
          onClose={() => {
            setActiveInvoice(null);
          }}
        />
      )}
    </div>
  );
}
